// src/screens/GoogleAds.js
// Google Ads campaign performance + spend pacing.
//
// Data source: scheduled function pulls Google Ads API → public.google_ads_campaigns
// daily. RPC \`admin_get_google_ads_summary\` rolls up MTD; \`admin_get_google_ads_campaigns\`
// returns per-campaign rows.
//
// Critical metric: CAC by campaign (spend / converted signups). Per CLAUDE.md, the
// $10 blended CAC target only works if Google Ads stays <15% of acquisition AND
// its own CAC stays under $80. Use this screen to enforce that weekly.

import React, { useState, useEffect } from 'react';
import { MetricCard, Card, CardTitle, Grid, Insight } from '../components/UI';
import { supabase } from '../supabaseClient';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
const fmt = (n) => (n == null ? '—' : n.toLocaleString());
const usd = (n) => (n == null ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`);

function Skel({ h = 12, mb = 10 }) {
  return (
    <div style={{ height: h, borderRadius: 6, marginBottom: mb,
      background: 'linear-gradient(90deg,#edf2f5 25%,#f5f7f9 50%,#edf2f5 75%)',
      backgroundSize: '200% 100%', animation: 'shimmer 1.6s ease infinite' }} />
  );
}

function ConnectGAds() {
  return (
    <Card>
      <CardTitle>Connect Google Ads</CardTitle>
      <div style={{ fontFamily: F, fontSize: 13, color: '#4e7f97', lineHeight: 1.6, padding: '8px 0 16px' }}>
        <p style={{ marginTop: 0 }}>
          No Google Ads data found yet. To wire this up:
        </p>
        <ol style={{ paddingLeft: 20, margin: '8px 0' }}>
          <li>Apply for Google Ads Developer Token (manager account required)</li>
          <li>OAuth a service account against the Sanoire Ads account</li>
          <li>Create a Supabase scheduled function that calls <code>GoogleAdsService.search</code> daily → writes campaign/keyword/spend rows to <code>public.google_ads_campaigns</code></li>
          <li>Implement RPCs: <code>admin_get_google_ads_summary(month)</code> and <code>admin_get_google_ads_campaigns(month)</code></li>
          <li>Join with <code>public.user_acquisition</code> (UTM tuples) to compute CAC per campaign</li>
        </ol>
        <p style={{ marginBottom: 0 }}>Per CLAUDE.md: keep Google Ads share &lt;15% of total acquisition and channel CAC &lt;$80, or pull spend back.</p>
      </div>
    </Card>
  );
}

export default function GoogleAds() {
  const [summary, setSummary] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [setupNeeded, setSetupNeeded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data: s, error: e1 } = await supabase.rpc('admin_get_google_ads_summary');
        const { data: c, error: e2 } = await supabase.rpc('admin_get_google_ads_campaigns');
        if (cancelled) return;
        if (e1 || e2 || !s) {
          setSetupNeeded(true);
        } else {
          setSummary(s);
          setCampaigns(c || []);
        }
      } catch (_e) {
        if (!cancelled) setSetupNeeded(true);
      }
      if (!cancelled) setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div style={{ fontFamily: F, padding: 20 }}>
        <Skel h={32} mb={20} />
        <Skel h={120} mb={16} />
        <Skel h={400} />
      </div>
    );
  }

  if (setupNeeded) {
    return (
      <div style={{ fontFamily: F, padding: 20 }}>
        <h1 style={{ fontSize: 24, fontWeight: 500, color: '#163040', margin: '0 0 4px' }}>Google Ads</h1>
        <p style={{ color: '#6F8FA7', fontSize: 13, margin: '0 0 24px' }}>Campaign performance, spend pacing, CAC by campaign</p>
        <ConnectGAds />
      </div>
    );
  }

  const cacWarning = summary?.cac > 80;

  return (
    <div style={{ fontFamily: F, padding: 20 }}>
      <h1 style={{ fontSize: 24, fontWeight: 500, color: '#163040', margin: '0 0 4px' }}>Google Ads</h1>
      <p style={{ color: '#6F8FA7', fontSize: 13, margin: '0 0 24px' }}>Month-to-date · Google Ads</p>

      <Grid>
        <MetricCard label="Spend MTD" value={usd(summary?.spend)} delta={summary?.spend_delta} />
        <MetricCard label="Clicks" value={fmt(summary?.clicks)} delta={summary?.clicks_delta} />
        <MetricCard label="Conversions" value={fmt(summary?.conversions)} delta={summary?.conversions_delta} />
        <MetricCard label="CAC (ads only)" value={usd(summary?.cac)}
          status={cacWarning ? 'warning' : 'ok'} delta={summary?.cac_delta ? -summary.cac_delta : null} />
      </Grid>

      {cacWarning && (
        <Insight title="Google Ads CAC above $80 threshold" status="warning">
          Per acquisition strategy: if Google Ads CAC exceeds $80 even on the best-performing long-tail keywords,
          shift budget to content or partnerships. Review the worst-performing campaigns below.
        </Insight>
      )}

      <Card style={{ marginTop: 20 }}>
        <CardTitle>Campaigns · MTD</CardTitle>
        <table style={{ width: '100%', fontSize: 12, marginTop: 12, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#6F8FA7', borderBottom: '.5px solid #d0dde7' }}>
              <th style={{ padding: '6px 8px' }}>Campaign</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Spend</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Clicks</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Conversions</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>CAC</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((c, i) => {
              const cac = c.conversions > 0 ? c.spend / c.conversions : null;
              const status = cac == null ? '—' : (cac < 30 ? 'Strong' : cac < 80 ? 'OK' : 'Cut');
              const color = status === 'Strong' ? '#1a7a3a' : status === 'OK' ? '#7a6210' : '#a52a2a';
              return (
                <tr key={i} style={{ borderBottom: '.5px solid #edf2f5' }}>
                  <td style={{ padding: '8px', color: '#163040' }}>{c.name}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{usd(c.spend)}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{fmt(c.clicks)}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{fmt(c.conversions)}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{cac == null ? '—' : usd(cac)}</td>
                  <td style={{ padding: '8px', textAlign: 'right', color, fontWeight: 600 }}>{status}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
